/**
 * Integrity hashes of the Hitz wire v1, computed exactly as the production game clients do
 * (docs/HITZ_WIRE_GAP.md §4). Synchronous SHA-256 so every message can be built in one call.
 * They chain what the client sends; they are not proof of a result (the client computes them).
 */
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
])
const H0 = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]
const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n))

export function sha256Hex(text: string): string {
  const bytes = new TextEncoder().encode(text)
  const len = Math.ceil((bytes.length + 9) / 64) * 64
  const buf = new Uint8Array(len)
  buf.set(bytes); buf[bytes.length] = 0x80
  const view = new DataView(buf.buffer)
  const bits = bytes.length * 8
  view.setUint32(len - 8, Math.floor(bits / 2 ** 32)); view.setUint32(len - 4, bits >>> 0)
  const h = Uint32Array.from(H0)
  const w = new Uint32Array(64)
  for (let off = 0; off < len; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4)
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3)
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0
    }
    let [a, b, c, d, e, f, g, hh] = h
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0
      hh = g; g = f; f = e; e = (d + t1) >>> 0; d = c; c = b; b = a; a = (t1 + t2) >>> 0
    }
    h[0] += a; h[1] += b; h[2] += c; h[3] += d; h[4] += e; h[5] += f; h[6] += g; h[7] += hh
  }
  return [...h].map((x) => x.toString(16).padStart(8, '0')).join('')
}

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))

export interface HashedActionFields {
  type: string; fromX?: number; fromY?: number; toX?: number; toY?: number; booster?: string; elapsedMs?: number
}

/** `sha256(previousHash \n sequence \n type|fromX|fromY|toX|toY|booster|elapsedMs)`; first action uses previousHash "". */
export function actionHash(previousHash: string, sequence: number, a: HashedActionFields): string {
  if (!Number.isSafeInteger(sequence) || sequence < 1) throw new RangeError('invalid action sequence')
  const fields = [a.type, a.fromX, a.fromY, a.toX, a.toY, a.booster, a.elapsedMs].map(str).join('|')
  return sha256Hex(`${previousHash}\n${sequence}\n${fields}`)
}

export interface HashedCheckpointFields {
  attemptId: string; levelId: string; levelVersion: string; actionSequence: number; boardState: string
  randomState: string; movesRemaining: number; score: number; goals: string; boosters?: string
}

export function checkpointHash(c: HashedCheckpointFields): string {
  return sha256Hex([c.attemptId, c.levelId, c.levelVersion, c.actionSequence, c.boardState, c.randomState,
    c.movesRemaining, c.score, c.goals, c.boosters].map(str).join('|'))
}
